import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Form, Modal, Table } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';
import { DocumentTemplateField, DocumentType } from '../types';
import { useAuth } from '../context/AuthContext';
import { useModal } from '../context/ModalContext';
import { formatCurrencyInputDisplay, normalizeCurrencyInput } from '../utils/currencyInput';

type FieldType = DocumentTemplateField['field_type'];

interface ScoreOption {
  label: string;
  weight: number;
}

interface FieldFormData {
  name: string;
  label: string;
  description: string;
  field_type: FieldType;
  options: string;
  allow_multiple: boolean;
  is_required: boolean;
  order: number;
  conditional_field_id?: number;
  conditional_operator?: 'equals' | 'greater_than' | 'greater_equal' | 'less_than' | 'less_equal' | 'not_equals' | 'contains' | 'between';
  conditional_value: string;
}

const DEFAULT_FORM: FieldFormData = {
  name: '',
  label: '',
  description: '',
  field_type: 'text_short',
  options: '',
  allow_multiple: false,
  is_required: false,
  order: 0,
  conditional_field_id: undefined,
  conditional_operator: 'equals',
  conditional_value: '',
};

const DocumentTypeFields: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showConfirm, showAlert } = useModal();
  const [documentType, setDocumentType] = useState<DocumentType | null>(null);
  const [fields, setFields] = useState<DocumentTemplateField[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingField, setEditingField] = useState<DocumentTemplateField | null>(null);
  const [formData, setFormData] = useState<FieldFormData>(DEFAULT_FORM);
  const [scoreOptions, setScoreOptions] = useState<ScoreOption[]>([]);

  const documentTypeId = Number(id);

  const parseFieldOptions = (options?: string): string[] => {
    if (!options) return [];

    try {
      const parsed = JSON.parse(options);
      if (Array.isArray(parsed)) {
        return parsed
          .map((item: any) => {
            if (typeof item === 'string') return item.trim();
            if (item && typeof item === 'object' && typeof item.label === 'string') {
              return item.label.trim();
            }
            return String(item ?? '').trim();
          })
          .filter((item) => item.length > 0);
      }
    } catch {
      // fallback para texto separado por vírgula/quebra de linha
    }

    return options
      .split(/[,\n]/)
      .map((opt) => opt.trim())
      .filter((opt) => opt.length > 0);
  };

  const getOperatorsForFieldType = (fieldType?: string): Array<{ value: FieldFormData['conditional_operator']; label: string }> => {
    switch (fieldType) {
      case 'text_short':
      case 'text_long':
      case 'link':
        return [
          { value: 'equals', label: 'Igual a' },
          { value: 'not_equals', label: 'Diferente de' },
          { value: 'contains', label: 'Contém' },
        ];
      case 'integer':
      case 'decimal':
      case 'currency':
      case 'score':
        return [
          { value: 'equals', label: 'Igual a' },
          { value: 'not_equals', label: 'Diferente de' },
          { value: 'greater_than', label: 'Maior que' },
          { value: 'greater_equal', label: 'Maior ou igual a' },
          { value: 'less_than', label: 'Menor que' },
          { value: 'less_equal', label: 'Menor ou igual a' },
          { value: 'between', label: 'Entre' },
        ];
      case 'date':
        return [
          { value: 'equals', label: 'Igual a' },
          { value: 'not_equals', label: 'Diferente de' },
          { value: 'greater_than', label: 'Depois de' },
          { value: 'greater_equal', label: 'Depois ou igual a' },
          { value: 'less_than', label: 'Antes de' },
          { value: 'less_equal', label: 'Antes ou igual a' },
          { value: 'between', label: 'Entre' },
        ];
      case 'list':
      case 'combo':
      case 'check':
        return [
          { value: 'equals', label: 'Igual a' },
          { value: 'not_equals', label: 'Diferente de' },
          { value: 'contains', label: 'Contém' },
        ];
      default:
        return [
          { value: 'equals', label: 'Igual a' },
          { value: 'not_equals', label: 'Diferente de' },
        ];
    }
  };

  const getDefaultOperatorForFieldType = (fieldType?: string): FieldFormData['conditional_operator'] => {
    if (['integer', 'decimal', 'currency', 'score'].includes(fieldType || '')) {
      return 'greater_equal';
    }
    return 'equals';
  };

  const fetchData = async () => {
    if (!documentTypeId) return;
    try {
      setLoading(true);
      setError(null);
      const [typeRes, fieldsRes] = await Promise.all([
        api.get(`/api/documents/types/${documentTypeId}`),
        api.get(`/api/documents/types/${documentTypeId}/fields`),
      ]);
      setDocumentType(typeRes.data);
      setFields(fieldsRes.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao carregar campos do tipo de documento.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [documentTypeId]);

  const openCreateModal = () => {
    setEditingField(null);
    setFormData({ ...DEFAULT_FORM, order: fields.length });
    setScoreOptions([]);
    setShowModal(true);
  };

  const openEditModal = (field: DocumentTemplateField) => {
    setEditingField(field);
    setFormData({
      name: field.name,
      label: field.label,
      description: field.description || '',
      field_type: field.field_type,
      options: field.options || '',
      allow_multiple: field.allow_multiple,
      is_required: field.is_required,
      order: field.order,
      conditional_field_id: field.conditional_field_id,
      conditional_operator: field.conditional_operator || 'equals',
      conditional_value: field.conditional_value || '',
    });
    
    // Se for campo score, deserializar opções com pesos
    if (field.field_type === 'score' && field.options) {
      try {
        const parsed = JSON.parse(field.options);
        if (Array.isArray(parsed)) {
          setScoreOptions(parsed);
        } else {
          setScoreOptions([]);
        }
      } catch {
        setScoreOptions([]);
      }
    } else {
      setScoreOptions([]);
    }
    
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!documentTypeId) return;
    
    // Validação para campo score
    if (formData.field_type === 'score') {
      if (scoreOptions.length === 0) {
        setError('Adicione pelo menos uma opção para o campo score.');
        return;
      }
      const hasEmptyLabel = scoreOptions.some(opt => !opt.label.trim());
      if (hasEmptyLabel) {
        setError('Todas as opções devem ter um texto.');
        return;
      }
      const hasInvalidWeight = scoreOptions.some(opt => opt.weight <= 0 || isNaN(opt.weight));
      if (hasInvalidWeight) {
        setError('Todos os pesos devem ser números positivos.');
        return;
      }
    }
    
    try {
      setError(null);
      
      // Para campo score, serializar opções como JSON
      let optionsValue = formData.options || null;
      if (formData.field_type === 'score') {
        optionsValue = JSON.stringify(scoreOptions);
      }

      const hasConditionalRule = Boolean(formData.conditional_field_id);
      
      const payload = {
        ...formData,
        description: formData.description || null,
        options: optionsValue,
        conditional_field_id: hasConditionalRule ? formData.conditional_field_id : null,
        conditional_operator: hasConditionalRule ? (formData.conditional_operator || 'equals') : null,
        conditional_value: hasConditionalRule ? (formData.conditional_value?.trim() || null) : null,
      };

      if (editingField) {
        await api.put(`/api/documents/types/${documentTypeId}/fields/${editingField.id}`, payload);
      } else {
        await api.post(`/api/documents/types/${documentTypeId}/fields`, {
          ...payload,
          document_type_id: documentTypeId,
        });
      }

      setShowModal(false);
      await fetchData();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao salvar campo.');
    }
  };

  const handleDelete = async (field: DocumentTemplateField) => {
    if (!documentTypeId) return;
    const ok = await showConfirm({
      title: 'Excluir campo',
      message: `Excluir o campo "${field.label}"?`,
      variant: 'danger',
      confirmLabel: 'Excluir',
      cancelLabel: 'Cancelar',
    });
    if (!ok) return;
    try {
      await api.delete(`/api/documents/types/${documentTypeId}/fields/${field.id}`);
      await fetchData();
      setError(null);
      showAlert({
        title: 'Campo excluído',
        message: `O campo "${field.label}" foi removido do tipo de documento. Os documentos já criados (rascunhos e finalizados) não foram alterados e mantêm os dados preenchidos anteriormente.`,
        variant: 'info',
      });
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao excluir campo.');
    }
  };

  const handleMoveField = async (fieldId: number, direction: 'up' | 'down') => {
    if (!documentTypeId) return;

    const ordered = [...fields].sort((a, b) => a.order - b.order);
    const currentIndex = ordered.findIndex((f) => f.id === fieldId);
    if (currentIndex < 0) return;

    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (targetIndex < 0 || targetIndex >= ordered.length) return;

    [ordered[currentIndex], ordered[targetIndex]] = [ordered[targetIndex], ordered[currentIndex]];

    // Reindexa ordem de forma sequencial e persiste no backend
    const reordered = ordered.map((field, index) => ({ ...field, order: index }));
    setFields(reordered);

    try {
      await Promise.all(
        reordered.map((field) =>
          api.put(`/api/documents/types/${documentTypeId}/fields/${field.id}`, { order: field.order })
        )
      );
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao reordenar campos.');
      await fetchData();
    }
  };

  if (user?.role !== 'admin' && user?.role !== 'analista') {
    return <Alert variant="warning">Você não tem permissão para acessar esta página.</Alert>;
  }

  const availableConditionalFields = [...fields]
    .filter((f) => !editingField || f.id !== editingField.id)
    .sort((a, b) => a.order - b.order);
  const selectedConditionalField = availableConditionalFields.find((f) => f.id === formData.conditional_field_id);
  const conditionalOperators = getOperatorsForFieldType(selectedConditionalField?.field_type);
  const conditionalFieldOptions = parseFieldOptions(selectedConditionalField?.options);

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h2 className="mb-1">Campos do Tipo de Documento</h2>
          <div className="text-muted">{documentType ? documentType.name : '...'}</div>
        </div>
        <div className="d-flex gap-2">
          <Button variant="secondary" onClick={() => navigate('/document-types')}>
            Voltar
          </Button>
          <Button variant="primary" onClick={openCreateModal}>
            Novo Campo
          </Button>
        </div>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      {loading ? (
        <p>Carregando...</p>
      ) : (
        <Card>
          <Card.Body>
            <Table striped bordered hover>
              <thead>
                <tr>
                  <th>Ordem</th>
                  <th>Nome</th>
                  <th>Rótulo</th>
                  <th>Tipo</th>
                  <th>Obrigatório</th>
                  <th>Mover</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {fields.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center">
                      Nenhum campo cadastrado.
                    </td>
                  </tr>
                ) : (
                  [...fields].sort((a, b) => a.order - b.order).map((field, index, arr) => (
                      <tr key={field.id}>
                        <td>{field.order}</td>
                        <td>{field.name}</td>
                        <td>{field.label}</td>
                        <td>{field.field_type}</td>
                        <td>{field.is_required ? 'Sim' : 'Não'}</td>
                        <td>
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            className="me-1"
                            disabled={index === 0}
                            onClick={() => handleMoveField(field.id, 'up')}
                            title="Mover para cima"
                          >
                            ↑
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            disabled={index === arr.length - 1}
                            onClick={() => handleMoveField(field.id, 'down')}
                            title="Mover para baixo"
                          >
                            ↓
                          </Button>
                        </td>
                        <td>
                          <Button size="sm" variant="outline-primary" className="me-2" onClick={() => openEditModal(field)}>
                            Editar
                          </Button>
                          <Button size="sm" variant="outline-danger" onClick={() => handleDelete(field)}>
                            Excluir
                          </Button>
                        </td>
                      </tr>
                    ))
                )}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{editingField ? 'Editar Campo' : 'Novo Campo'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Nome técnico *</Form.Label>
              <Form.Control
                value={formData.name}
                onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="ex: memorial_objetivo"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Rótulo *</Form.Label>
              <Form.Control
                value={formData.label}
                onChange={(e) => setFormData((prev) => ({ ...prev, label: e.target.value }))}
                placeholder="Texto exibido ao usuário"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Tipo *</Form.Label>
              <Form.Select
                value={formData.field_type}
                onChange={(e) => {
                  const newType = e.target.value as FieldType;
                  setFormData((prev) => ({ ...prev, field_type: newType }));
                  // Resetar opções de score se mudar de tipo
                  if (newType !== 'score') {
                    setScoreOptions([]);
                  } else if (formData.field_type !== 'score') {
                    // Se está mudando PARA score, inicializar com uma opção vazia
                    setScoreOptions([{ label: '', weight: 1 }]);
                  }
                }}
              >
                <option value="instruction_text">Texto de Instrução</option>
                <option value="text_short">Texto Curto</option>
                <option value="text_long">Texto Longo</option>
                <option value="list">Lista</option>
                <option value="combo">Combo</option>
                <option value="date">Data</option>
                <option value="period">Período</option>
                <option value="link">Link</option>
                <option value="integer">Número Inteiro</option>
                <option value="decimal">Número Decimal</option>
                <option value="currency">Moeda</option>
                <option value="check">Checkbox</option>
                <option value="file">Arquivo</option>
                <option value="files">Múltiplos Arquivos</option>
                <option value="score">Score</option>
              </Form.Select>
            </Form.Group>
            {formData.field_type === 'score' && (
              <Form.Group className="mb-3">
                <Form.Label>Opções com Pesos *</Form.Label>
                <div className="border rounded p-3" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  {scoreOptions.map((opt, index) => (
                    <div key={index} className="d-flex gap-2 mb-2 align-items-center">
                      <Form.Control
                        type="text"
                        placeholder="Texto da opção"
                        value={opt.label}
                        onChange={(e) => {
                          const updated = [...scoreOptions];
                          updated[index].label = e.target.value;
                          setScoreOptions(updated);
                        }}
                        className="flex-grow-1"
                      />
                      <Form.Control
                        type="number"
                        placeholder="Peso"
                        min="0.1"
                        step="0.1"
                        value={opt.weight}
                        onChange={(e) => {
                          const updated = [...scoreOptions];
                          updated[index].weight = Number(e.target.value) || 1;
                          setScoreOptions(updated);
                        }}
                        style={{ width: '100px' }}
                      />
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => {
                          setScoreOptions(scoreOptions.filter((_, i) => i !== index));
                        }}
                      >
                        Remover
                      </Button>
                    </div>
                  ))}
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => {
                      setScoreOptions([...scoreOptions, { label: '', weight: 1 }]);
                    }}
                  >
                    + Adicionar Opção
                  </Button>
                </div>
                <Form.Text className="text-muted">
                  Para cada opção, o usuário dará uma nota de 1 a 5. O peso define a importância de cada opção no cálculo do score final.
                </Form.Text>
              </Form.Group>
            )}
            {(formData.field_type === 'list' ||
              formData.field_type === 'combo' ||
              formData.field_type === 'check') && (
              <Form.Group className="mb-3">
                <Form.Label>Opções</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={formData.options}
                  onChange={(e) => setFormData((prev) => ({ ...prev, options: e.target.value }))}
                  placeholder={
                    formData.field_type === 'check'
                      ? 'Uma opção por linha ou separadas por vírgula. Deixe vazio para checkbox simples (marcado/desmarcado). Com opções preenchidas, use "Permitir marcar mais de uma opção" abaixo para definir se o usuário escolhe uma ou várias.'
                      : 'Uma opção por linha ou separadas por vírgula'
                  }
                />
                {formData.field_type === 'check' && (
                  <Form.Text className="text-muted">
                    Com opções preenchidas, o campo exibe uma lista de escolhas. Use a opção abaixo para permitir marcar apenas uma ou várias.
                  </Form.Text>
                )}
              </Form.Group>
            )}
            <Form.Group className="mb-3">
              <Form.Label>Descrição</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                placeholder={
                  formData.field_type === 'instruction_text'
                    ? 'Texto que será exibido ao usuário como instrução.'
                    : undefined
                }
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Ordem</Form.Label>
              <Form.Control
                type="number"
                value={formData.order}
                onChange={(e) => setFormData((prev) => ({ ...prev, order: Number(e.target.value || 0) }))}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Exibição Condicional</Form.Label>
              <Form.Select
                value={formData.conditional_field_id || ''}
                onChange={(e) => {
                  const newFieldId = e.target.value ? Number(e.target.value) : undefined;
                  const conditionalField = availableConditionalFields.find((f) => f.id === newFieldId);
                  const nextOptions = parseFieldOptions(conditionalField?.options);
                  const nextValue = nextOptions.length > 0 ? nextOptions[0] : '';
                  setFormData((prev) => ({
                    ...prev,
                    conditional_field_id: newFieldId,
                    conditional_operator: newFieldId ? getDefaultOperatorForFieldType(conditionalField?.field_type) : 'equals',
                    conditional_value: newFieldId ? nextValue : '',
                  }));
                }}
              >
                <option value="">Sempre visível</option>
                {availableConditionalFields.map((field) => (
                  <option key={field.id} value={field.id}>
                    {field.label} ({field.field_type})
                  </option>
                ))}
              </Form.Select>
              <Form.Text className="text-muted">
                Exibe este campo apenas quando a regra abaixo for atendida.
              </Form.Text>
            </Form.Group>
            {formData.conditional_field_id && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>Operador</Form.Label>
                  <Form.Select
                    value={formData.conditional_operator || 'equals'}
                    onChange={(e) => {
                      const nextOp = e.target.value as FieldFormData['conditional_operator'];
                      const switchingToBetween = nextOp === 'between';
                      const switchingFromBetween = formData.conditional_operator === 'between' && !switchingToBetween;
                      setFormData((prev) => ({
                        ...prev,
                        conditional_operator: nextOp,
                        conditional_value: switchingToBetween || switchingFromBetween
                          ? (switchingToBetween ? '{"min":"","max":""}' : '')
                          : prev.conditional_value,
                      }));
                    }}
                  >
                    {conditionalOperators.map((op) => (
                      <option key={op.value} value={op.value}>
                        {op.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Valor que ativa a exibição</Form.Label>
                  {formData.conditional_operator === 'between' &&
                  selectedConditionalField &&
                  ['integer', 'decimal', 'currency', 'score', 'date'].includes(selectedConditionalField.field_type) ? (
                    (() => {
                      let min = '';
                      let max = '';
                      try {
                        const parsed = JSON.parse(formData.conditional_value || '{}');
                        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                          min = parsed.min != null ? String(parsed.min) : '';
                          max = parsed.max != null ? String(parsed.max) : '';
                        }
                      } catch {
                        // ignore
                      }
                      const isDate = selectedConditionalField.field_type === 'date';
                      const isInteger = selectedConditionalField.field_type === 'integer';
                      const usesBrDecimal = !isDate && !isInteger;
                      const setBounds = (nextMin: string, nextMax: string) => {
                        setFormData((prev) => ({
                          ...prev,
                          conditional_value: JSON.stringify({ min: nextMin.trim(), max: nextMax.trim() }),
                        }));
                      };
                      const onBoundChange = (side: 'min' | 'max', raw: string) => {
                        if (usesBrDecimal) {
                          const normalized = normalizeCurrencyInput(raw);
                          if (normalized === null) return;
                          setBounds(side === 'min' ? normalized : min, side === 'max' ? normalized : max);
                          return;
                        }
                        setBounds(side === 'min' ? raw : min, side === 'max' ? raw : max);
                      };
                      return (
                        <div className="d-flex gap-2 align-items-end flex-wrap">
                          <div className="flex-grow-1" style={{ minWidth: 120 }}>
                            <Form.Label className="mb-1">De</Form.Label>
                            <Form.Control
                              type={isDate ? 'date' : isInteger ? 'number' : 'text'}
                              inputMode={isDate ? undefined : 'decimal'}
                              step={isInteger ? '1' : undefined}
                              value={usesBrDecimal ? formatCurrencyInputDisplay(min, 'BRL') : min}
                              onChange={(e) => onBoundChange('min', e.target.value)}
                              placeholder={usesBrDecimal ? 'Ex.: 30000,01' : undefined}
                            />
                          </div>
                          <div className="flex-grow-1" style={{ minWidth: 120 }}>
                            <Form.Label className="mb-1">Até</Form.Label>
                            <Form.Control
                              type={isDate ? 'date' : isInteger ? 'number' : 'text'}
                              inputMode={isDate ? undefined : 'decimal'}
                              step={isInteger ? '1' : undefined}
                              value={usesBrDecimal ? formatCurrencyInputDisplay(max, 'BRL') : max}
                              onChange={(e) => onBoundChange('max', e.target.value)}
                              placeholder={usesBrDecimal ? 'Ex.: 139999,99' : undefined}
                            />
                          </div>
                        </div>
                      );
                    })()
                  ) : (selectedConditionalField?.field_type === 'list' ||
                    selectedConditionalField?.field_type === 'combo' ||
                    (selectedConditionalField?.field_type === 'check' && conditionalFieldOptions.length > 0)) ? (
                    conditionalFieldOptions.length > 0 ? (
                      <Form.Select
                        value={formData.conditional_value}
                        onChange={(e) => setFormData((prev) => ({ ...prev, conditional_value: e.target.value }))}
                      >
                        <option value="">Selecione a opção...</option>
                        {conditionalFieldOptions.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </Form.Select>
                    ) : (
                      <Form.Control
                        type="text"
                        value={formData.conditional_value}
                        onChange={(e) => setFormData((prev) => ({ ...prev, conditional_value: e.target.value }))}
                        placeholder="Ex: valor que ativa"
                      />
                    )
                  ) : selectedConditionalField?.field_type === 'check' ? (
                    <Form.Select
                      value={formData.conditional_value || 'true'}
                      onChange={(e) => setFormData((prev) => ({ ...prev, conditional_value: e.target.value }))}
                    >
                      <option value="true">Marcado</option>
                      <option value="false">Desmarcado</option>
                    </Form.Select>
                  ) : (
                    <Form.Control
                      type={selectedConditionalField?.field_type === 'date' ? 'date' : 'text'}
                      value={formData.conditional_value}
                      onChange={(e) => setFormData((prev) => ({ ...prev, conditional_value: e.target.value }))}
                      placeholder="Ex: aprovado"
                    />
                  )}
                  <Form.Text className="text-muted">
                    {formData.conditional_operator === 'between'
                      ? 'Exibe este campo quando o valor estiver entre De e Até (inclusive).'
                      : selectedConditionalField?.field_type === 'check'
                      ? conditionalFieldOptions.length > 0
                        ? 'Selecione qual opção do checkbox deve estar marcada (ou na escolha única) para exibir este campo.'
                        : 'Checkbox simples: exibir quando estiver marcado ou desmarcado.'
                      : 'Exemplo: para list/combo/checkbox com opções, selecione a opção que ativa a exibição.'}
                  </Form.Text>
                </Form.Group>
              </>
            )}
            <Form.Check
              className="mb-2"
              label="Obrigatório"
              checked={formData.is_required}
              onChange={(e) => setFormData((prev) => ({ ...prev, is_required: e.target.checked }))}
              disabled={formData.field_type === 'instruction_text'}
            />
            {(formData.field_type === 'list' || formData.field_type === 'combo' || formData.field_type === 'check') && (
              <>
                <Form.Check
                  label={
                    formData.field_type === 'check'
                      ? 'Permitir marcar mais de uma opção'
                      : 'Permitir múltiplos valores'
                  }
                  checked={formData.allow_multiple}
                  onChange={(e) => setFormData((prev) => ({ ...prev, allow_multiple: e.target.checked }))}
                />
                {formData.field_type === 'check' && (
                  <Form.Text className="text-muted d-block mb-2">
                    Se desmarcado, o usuário poderá escolher apenas uma opção (comportamento de escolha única). Se marcado, poderá marcar várias opções.
                  </Form.Text>
                )}
              </>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleSave}>
            Salvar
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default DocumentTypeFields;
